/**
 * Sandbox List Page
 *
 * Hosts the v1 (template-first) and v2 (application-first) sandbox views. A
 * v1 | v2 toggle shows when the user has both flags and the org has v1 sandboxes
 * (an org with none sees v2 only); the URL's `?view=` wins over the saved
 * default, and only "Set as default" saves it.
 *
 * @module features/sandboxes/pages/SandboxListPage
 */

import React from 'react';
import { Button, Flex, LoadingOrbit, Page, PageHeader, PillToggle, type PillToggleOption } from '@chatsdet/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useEnvironmentTemplates } from '../../../hooks/useEnvironmentTemplates';
import { useSandboxesViewPrefs } from '../../../hooks/useUserSettings';
import { SandboxV1View } from '../components/SandboxV1View';
import { SandboxV2View } from '../components/v2/SandboxV2View';
import {
  resolveSandboxesView,
  validateSandboxesListSearch,
  type SandboxesView,
} from '../sandboxesRouteSearch';

const VIEW_OPTIONS: readonly PillToggleOption<SandboxesView>[] = [
  { value: 'v1', label: 'v1' },
  { value: 'v2', label: 'v2' },
];

export const SandboxListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The route's own validator already ran; strict:false avoids importing the router (a cycle through routes.ts).
  const search = validateSandboxesListSearch(useSearch({ strict: false }));
  const { defaultView, setDefaultView } = useSandboxesViewPrefs();

  const hasV1 = Boolean(user?.features?.environments);
  const hasV2 = Boolean(user?.features?.sandboxV2);
  // "No templates" is "no v1 sandboxes": environment_instances.template_id is NOT NULL and cascades on delete
  // (packages/db/src/schema/foundation-core.ts), and page one of the list is enough to know it is non-empty.
  // Only orgs with both flags need the answer.
  const templatesQuery = useEnvironmentTemplates({ enabled: hasV1 && hasV2 });
  const awaitingV1Sandboxes = hasV1 && hasV2 && templatesQuery.isLoading;
  // With nothing loaded, a failed request keeps today's behaviour (the toggle) rather than hiding v1 from an org that
  // may have sandboxes. Data already loaded wins over a later failed refetch, so the view cannot flip.
  const hasV1Templates = templatesQuery.data ? templatesQuery.data.data.length > 0 : templatesQuery.isError;
  const view = resolveSandboxesView({ requested: search.view, savedDefault: defaultView, hasV1, hasV2, hasV1Templates });

  React.useEffect(() => {
    if (user && view === null) {
      void navigate({ to: '/' });
    }
  }, [user, view, navigate]);

  if (awaitingV1Sandboxes) {
    return (
      <Page>
        <PageHeader title="Test Environments" />
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }

  if (view === null) {
    return null;
  }

  const switcher =
    hasV1 && hasV2 && hasV1Templates ? (
      <Flex align="center" gap="sm">
        {view !== defaultView && (
          <Button variant="ghost" size="sm" onPress={() => setDefaultView(view)}>
            Set as default
          </Button>
        )}
        <PillToggle
          aria-label="Sandboxes version"
          value={view}
          onChange={(next) => void navigate({ to: '/sandboxes', search: { view: next } })}
          options={VIEW_OPTIONS}
        />
      </Flex>
    ) : null;

  return view === 'v2' ? <SandboxV2View switcher={switcher} /> : <SandboxV1View switcher={switcher} />;
};
